/**
 * @module utils/columnSort/getEffective
 * @description 列序展示入口：按持久化状态选择场景 1 推导或场景 3 对齐后的列表
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  buildColumnSortFromQB,
  type IBuildColumnSortFromQBInput,
} from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/fromQB/buildFromQB';
import type { IColumnPercentageItem } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/columnPercentage';
import type { IDimensionPercentageConfig } from '../../../types/settings/dimensionPercentage';
import {
  dedupeColumnSortList,
  hasPersistedColumnSort,
  normalizeColumnSortItem,
} from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/keys';
import { applyCompareTimeToColumnSort } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/compareTimeUserFilter';
import { syncColumnSortVirtualColumns } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/syncColumnSortVirtualColumns';

export interface IColumnSortStateSlice {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
  hasDraft?: Record<string, boolean>;
}

function resolveColumnPercentage(
  state: IColumnSortStateSlice,
): IColumnPercentageItem[] | undefined {
  const raw = state.draft.column_percentage ?? state.base.column_percentage;
  return Array.isArray(raw) ? (raw as IColumnPercentageItem[]) : undefined;
}

function resolveDimensionPercentage(
  state: IColumnSortStateSlice,
): IDimensionPercentageConfig | undefined {
  const raw = state.draft.dimension_percentage ?? state.base.dimension_percentage;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  return raw as IDimensionPercentageConfig;
}

/**
 *  * 面板展示用列序（不落库）
 * - 无 column_sort：buildFromQB，内含 column_percentage → % 虚拟列
 * - draft 有 column_sort：严格按 draft 顺序（面板拖拽 / 列占比 reducer 写入），不做 withQBReconcile，
 *   且不跑 applyCompareTimeToColumnSort，避免对比时间补丁改序/删项导致 QuickDragList 受控回弹
 *   （自定义字段等 table_id 为空时尤易误删）
 * - 仅 base 有 column_sort：严格按 base 顺序展示（保存的图表为准）；
 *   QB 增删字段由 linkage sanitize 写入 base，展示层不再 reconcile 重排
 * 已持久化 column_sort 展示：先同步列占比 %，再同步维度占比 dim_pct（源 metric → dim_pct → %）
 */
function readPersistedBaseColumnSort(
  base: Record<string, unknown>,
  columnPercentage: IColumnPercentageItem[] | undefined,
  dimensionPercentage: IDimensionPercentageConfig | undefined,
): IColumnSortItem[] {
  const baseList = dedupeColumnSortList(base.column_sort as IColumnSortItem[]);
  return syncColumnSortVirtualColumns(
    baseList.map(item => normalizeColumnSortItem(item)),
    columnPercentage,
    dimensionPercentage,
  );
}

function readDraftColumnSortForDisplay(
  draft: Record<string, unknown>,
): IColumnSortItem[] | undefined {
  if (
    !('column_sort' in draft) ||
    !Array.isArray(draft.column_sort) ||
    draft.column_sort.length === 0
  ) {
    return undefined;
  }
  return dedupeColumnSortList(draft.column_sort as IColumnSortItem[]).map(
    item => normalizeColumnSortItem(item),
  );
}

export function getEffectiveColumnSort(
  state: IColumnSortStateSlice,
  qbInput: IBuildColumnSortFromQBInput,
): IColumnSortItem[] {
  const columnPercentage =
    resolveColumnPercentage(state) ?? qbInput.columnPercentage;
  const dimensionPercentage =
    resolveDimensionPercentage(state) ?? qbInput.dimensionPercentage;
  const qbWithPct: IBuildColumnSortFromQBInput = {
    ...qbInput,
    columnPercentage,
    dimensionPercentage,
  };

  let list: IColumnSortItem[];

  const draftDisplay = readDraftColumnSortForDisplay(state.draft);
  if (draftDisplay) {
    // 面板拖拽后仍同步虚拟列，使 dim_pct / % 紧跟各自源 metric（拖拽单条 metric 时不会丢列）
    return syncColumnSortVirtualColumns(
      draftDisplay,
      columnPercentage,
      dimensionPercentage,
    );
  }

  if (hasPersistedColumnSort({ base: state.base, draft: state.draft })) {
    const pctForBase =
      'column_percentage' in state.draft ? columnPercentage : undefined;
    const dimPctForBase =
      'dimension_percentage' in state.draft ? dimensionPercentage : undefined;
    list = readPersistedBaseColumnSort(state.base, pctForBase, dimPctForBase);
  } else {
    list = buildColumnSortFromQB(qbWithPct);
  }

  // base / QB 推导路径：按当前 QB 对比时间状态刷新 is_compare_date（关闭时从 QB 还原 display）
  return applyCompareTimeToColumnSort(
    list,
    qbWithPct.compareTime,
    qbWithPct.columnDimensions || [],
    qbWithPct.columnFilters,
  );
}
